import { createFileRoute, redirect } from "@tanstack/react-router";

import { OfferPageChrome } from "~/components/offer-page/offer-page-chrome";
import {
  CoachContactCard,
  CoachWheelHeader,
  CoachWheelWhereToStart,
} from "~/components/wheel/coach-page";
import { WheelActionBar } from "~/components/wheel/results/action-bar";
import { WheelResultsLayout } from "~/components/wheel/results/results-layout";
import { recordCoachWheelContactClickFn } from "~/lib/functions/coach-leads";
import { coachWheelPath } from "~/lib/shared/coach-wheel";
import { createBrandMeta } from "~/lib/shared/meta";
import { whereToStart } from "~/lib/shared/nova/wheel-page-copy";

/**
 * Results of a Wheel taken on a coach's page. Shown only under that coach's
 * URL and while their page may show it to this viewer; otherwise the visitor
 * goes (302) to the platform results for the same id and keeps their result.
 * Instead of the platform's intake box, content suggestions and "Save to my
 * account", it offers one button to this coach, after her next steps for the
 * visitor's two lowest areas when she has approved NOVA text. After lead
 * capture it says the results email is on its way, or that the results were
 * saved.
 */
interface CoachResultsSearch {
  /** The visitor shared their details, so the results email is on its way. */
  emailed?: true;
  /** The visitor arrived through "Save my results", signed in. */
  saved?: true;
}

export const Route = createFileRoute("/$username/wheel-of-life/results/$id")({
  validateSearch: (search: Record<string, unknown>): CoachResultsSearch => ({
    ...(search.emailed === true ? { emailed: true as const } : {}),
    ...(search.saved === true ? { saved: true as const } : {}),
  }),
  loader: async ({ params }) => {
    const { getCoachWheelResultFn } = await import("~/lib/functions/wheel");
    const result = await getCoachWheelResultFn({
      data: { username: params.username, id: params.id },
    });
    if (result.kind === "platform") {
      throw redirect({
        to: "/wheel-of-life/results/$id",
        params: { id: params.id },
        search: { claim: undefined },
        statusCode: 302,
      });
    }
    return { page: result.page, session: result.session };
  },
  staticData: { title: "Wheel of Life Results" },
  head: ({ params, loaderData }) => {
    const name = loaderData?.page.coach.fullName;
    return createBrandMeta({
      title: "My Wheel of Life",
      description: name
        ? `My Wheel of Life, taken with ${name}.`
        : "See how my eight life areas are stacking up.",
      ogTitle: "My Wheel of Life",
      ogImage: `/api/og/wheel/${params.id}`,
      ogType: "website",
      twitterCard: "summary_large_image",
      noindex: true,
    });
  },
  component: CoachResultsPage,
});

function CoachResultsPage(): React.JSX.Element {
  const { page, session } = Route.useLoaderData();
  const params = Route.useParams();
  const search = Route.useSearch();
  const base = coachWheelPath(page.coach.username);

  return (
    <OfferPageChrome>
      <WheelResultsLayout
        id={params.id}
        session={session}
        top={
          <>
            <CoachWheelHeader coach={page.coach} isLive={page.isLive} />
            {search.saved && (
              <p
                role="status"
                className="mx-auto mt-4 max-w-xl rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-200"
              >
                <span className="font-medium">Saved to your account.</span> Your
                Wheel of Life results are now in your Dream Coach Match account.
              </p>
            )}
          </>
        }
        actions={
          <WheelActionBar
            id={params.id}
            pdfHref={`${base}/results/${params.id}/print`}
            rescoreHref={`${base}?from=${params.id}`}
            saveHref={null}
          />
        }
      >
        <CoachWheelWhereToStart
          steps={page.copy ? whereToStart(session, page.copy.nextSteps) : []}
        />
        {page.contact && (
          <CoachContactCard
            coach={page.coach}
            contact={page.contact}
            line={page.copy?.contactLine ?? null}
            onContactClick={() =>
              recordCoachWheelContactClickFn({ data: { sessionId: params.id } })
            }
          />
        )}
        {search.emailed && !search.saved && (
          <p
            role="status"
            className="mt-6 rounded-xl bg-purple-50 px-4 py-3 text-sm leading-relaxed text-purple-950 ring-1 ring-purple-200"
          >
            We've emailed you a link to save your results to a free account.{" "}
            {page.coach.firstName} has your results too.
          </p>
        )}
      </WheelResultsLayout>
    </OfferPageChrome>
  );
}
